import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Section, Tile, Chip, impactTone, effortTone } from "@/growth/components/_ui";
import * as gc from "@/growth/api";

export default function AuditTab({ wsId, defaultUrl, onChanged }) {
  const [url, setUrl] = useState(defaultUrl || "");
  const [running, setRunning] = useState(false);
  const [audit, setAudit] = useState(null);

  const loadLatest = async () => {
    const r = await gc.latestAudit(wsId);
    if (r.audit) {
      setAudit(r.audit);
      if (!url) setUrl(r.audit.website_url);
    }
  };
  useEffect(() => { loadLatest(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doRun = async () => {
    if (!url) { toast.error("Add a URL first"); return; }
    setRunning(true);
    try {
      const r = await gc.runAudit({ workspace_id: wsId, website_url: url });
      setAudit(r);
      toast.success(`Audit complete — score ${r.overall_score}. ${r.actions.length} actions ranked.`);
      onChanged?.();
    } catch { toast.error("Audit failed"); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <Section title="Unified Growth Audit" subtitle="Combines website, SEO, and structure signals into one workspace, then ranks the five actions most likely to move your business.">
        <Card className="p-5 border border-slate-200 rounded-2xl">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-end">
            <div className="flex-1">
              <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Website URL</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your.site" className="mt-1" data-testid="gc-audit-url" />
            </div>
            <Button onClick={doRun} disabled={running} className="bg-slate-900 hover:bg-slate-800 rounded-full md:min-w-[160px]" data-testid="gc-audit-run-btn">
              {running ? "Running…" : "Run audit"}
            </Button>
          </div>
        </Card>
      </Section>

      {audit && (
        <>
          <Section title={`Score · ${audit.overall_score}/100`} subtitle={`Analyzed ${audit.website_url}`}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(audit.scores || {}).map(([k, v]) => (
                <Tile key={k} label={k.replace(/_/g, " ")} value={v} tone={v >= 70 ? "emerald" : v >= 40 ? "amber" : "rose"} />
              ))}
            </div>
          </Section>

          <Section title="Top 5 Ranked Actions" subtitle="Each action opens as a pending recommendation in the Approvals tab.">
            <div className="space-y-3">
              {(audit.top_actions || audit.actions || []).map((a, i) => (
                <Card key={i} className="p-5 border border-slate-200 rounded-2xl" data-testid={`gc-audit-action-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl font-black text-slate-300 leading-none">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1">
                      <div className="font-bold text-slate-900">{a.title}</div>
                      <div className="text-sm text-slate-600 mt-1">{a.summary}</div>
                      <div className="mt-2 text-xs text-slate-500"><strong>Evidence:</strong> {a.evidence}</div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Chip tone={impactTone(a.impact)}>Impact · {a.impact}</Chip>
                        <Chip tone={effortTone(a.effort)}>Effort · {a.effort}</Chip>
                        <Chip tone="indigo">Confidence · {Math.round((a.confidence || 0) * 100)}%</Chip>
                        <Chip tone="sky">{a.channel}</Chip>
                        {a.success_metric && <Chip tone="slate">Metric · {a.success_metric}</Chip>}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
